import { ROTATION_RAMP_DAYS } from '@gozoki/api-types'
import { addDaysIso } from '@gozoki/tools'
import { DateTime } from 'luxon'
import { frenchDayMonthOfIso } from '~/lib/iso-date'
import type {
  ProductRotationDto,
  RotationInput,
  RotationPreviewDto,
  RotationProductDto,
  RotationStatus,
  RotationStoreException,
} from '~/lib/product-rotations-query'
import { matchesTextSearch } from '~/lib/text-search'
import { pluralize } from '~/lib/utils'

/**
 * Logique pure des écrans Rotations (spec `2026-10-24-rotation-produits-design.md` §8) : matrice de
 * report, conversion pourcentages ↔ poids, corps envoyé à l'API, résumés de la liste, bandeaux de
 * la fiche produit et infobulle des Prévisions. Testée par `rotation-model.test.mjs`.
 */

export const PARIS = 'Europe/Paris'

export const ROTATION_STATUS_LABELS: Record<RotationStatus, string> = {
  upcoming: 'À venir',
  current: 'En cours',
  done: 'Terminée',
}

/** Libellés des puces de filtre, au pluriel. */
export const ROTATION_STATUS_FILTER_LABELS: Record<RotationStatus, string> = {
  upcoming: 'À venir',
  current: 'En cours',
  done: 'Terminées',
}

export const ROTATION_STATUSES = [
  'upcoming',
  'current',
  'done',
] as const satisfies readonly RotationStatus[]

// ------------------------------------------------------------------------------------------------
// Pourcentages ↔ poids
// ------------------------------------------------------------------------------------------------

/**
 * Les poids sont stockés sur 5 décimales (`numeric(6,5)`, 100 % → 1) : un pourcentage se saisit au
 * millième de point. Les calculs se font en millièmes de point entiers, sans bruit flottant.
 */
const MILLI_PER_PERCENT = 1000
const FULL_MILLI = 100 * MILLI_PER_PERCENT

const milliOf = (percent: number): number => Math.round(percent * MILLI_PER_PERCENT)

export const percentToWeight = (percent: number): number => milliOf(percent) / FULL_MILLI

export const weightToPercent = (weight: number): number =>
  Math.round(weight * FULL_MILLI) / MILLI_PER_PERCENT

/** `33,333` ou `33.333` → 33.333 ; vide ou illisible → `null`. Arrondi au millième de point. */
export const parsePercentText = (text: string): number | null => {
  const normalized = text.replace(/\s|%/g, '').replace(',', '.')
  if (normalized === '') return null
  if (!/^\d*\.?\d*$/.test(normalized)) return null
  const value = Number(normalized)
  return Number.isFinite(value) ? milliOf(value) / MILLI_PER_PERCENT : null
}

const percentFormat = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 3 })

/** `33,333 %`, `100 %`. */
export const formatPercent = (percent: number): string => `${percentFormat.format(percent)} %`

// ------------------------------------------------------------------------------------------------
// Formulaire et matrice de report
// ------------------------------------------------------------------------------------------------

/** Cellule de la matrice : part (en %) de la demande d'un sortant transmise à un entrant. */
export interface TransferCell {
  sourceProductId: number
  incomingProductId: number
  percent: number
}

export interface AnalogueLink {
  incomingProductId: number
  sourceProductId: number
}

export interface RotationFormValues {
  label: string
  /** Date N, `YYYY-MM-DD` ; `null` tant qu'elle n'est pas choisie. */
  rotationDate: string | null
  note: string
  outgoingProductIds: number[]
  incomingProductIds: number[]
  transfers: TransferCell[]
  analogues: AnalogueLink[]
}

export const emptyRotationValues = (): RotationFormValues => ({
  label: '',
  rotationDate: null,
  note: '',
  outgoingProductIds: [],
  incomingProductIds: [],
  transfers: [],
  analogues: [],
})

export const cellKey = (sourceProductId: number, incomingProductId: number): string =>
  `${sourceProductId}:${incomingProductId}`

export const cellPercent = (
  cells: readonly TransferCell[],
  sourceProductId: number,
  incomingProductId: number,
): number | null =>
  cells.find(
    (cell) =>
      cell.sourceProductId === sourceProductId && cell.incomingProductId === incomingProductId,
  )?.percent ?? null

/** Pose (ou retire, à `null` ou 0) la part d'une cellule. */
export const setCellPercent = (
  cells: readonly TransferCell[],
  sourceProductId: number,
  incomingProductId: number,
  percent: number | null,
): TransferCell[] => {
  const others = cells.filter(
    (cell) =>
      cell.sourceProductId !== sourceProductId || cell.incomingProductId !== incomingProductId,
  )
  if (percent === null || percent <= 0) return others
  return [...others, { sourceProductId, incomingProductId, percent }]
}

/** « 1 → 1 dans l'ordre » : le i-ème sortant transmet 100 % au i-ème entrant, rien d'autre. */
export const oneToOneInOrder = (
  outgoingProductIds: readonly number[],
  incomingProductIds: readonly number[],
): TransferCell[] =>
  outgoingProductIds.slice(0, incomingProductIds.length).map((sourceProductId, index) => ({
    sourceProductId,
    incomingProductId: incomingProductIds[index],
    percent: 100,
  }))

/**
 * « Répartir également » : chaque sortant donne 1/N de sa demande à chacun des N entrants. La part
 * est tronquée au millième de point pour que la ligne ne dépasse jamais 100 % (3 entrants :
 * 33,333 % chacun, 99,999 % transmis).
 */
export const spreadEvenly = (
  outgoingProductIds: readonly number[],
  incomingProductIds: readonly number[],
): TransferCell[] => {
  if (incomingProductIds.length === 0) return []
  const percent = Math.floor(FULL_MILLI / incomingProductIds.length) / MILLI_PER_PERCENT
  return outgoingProductIds.flatMap((sourceProductId) =>
    incomingProductIds.map((incomingProductId) => ({
      sourceProductId,
      incomingProductId,
      percent,
    })),
  )
}

/** Σ des parts transmises par un sortant, en %. */
export const rowTotalPercent = (cells: readonly TransferCell[], sourceProductId: number): number =>
  cells
    .filter((cell) => cell.sourceProductId === sourceProductId)
    .reduce((total, cell) => total + milliOf(cell.percent), 0) / MILLI_PER_PERCENT

export type RowState =
  | { kind: 'none'; prorata: 100 }
  | { kind: 'partial'; prorata: number }
  | { kind: 'full'; prorata: 0 }
  | { kind: 'over'; excess: number }

/**
 * Colonne « Transmis » d'une ligne : rien de dirigé (100 % au prorata), une partie (le reste au
 * prorata), tout, ou plus de 100 % (erreur, l'enregistrement est refusé).
 */
export const rowStateOf = (totalPercent: number): RowState => {
  const total = milliOf(totalPercent)
  if (total <= 0) return { kind: 'none', prorata: 100 }
  if (total === FULL_MILLI) return { kind: 'full', prorata: 0 }
  if (total > FULL_MILLI) return { kind: 'over', excess: (total - FULL_MILLI) / MILLI_PER_PERCENT }
  return { kind: 'partial', prorata: (FULL_MILLI - total) / MILLI_PER_PERCENT }
}

/** Sortants dont les parts dépassent 100 % : l'enregistrement est refusé tant qu'il en reste. */
export const overflowingSources = (values: RotationFormValues): number[] =>
  values.outgoingProductIds.filter(
    (productId) => rowStateOf(rowTotalPercent(values.transfers, productId)).kind === 'over',
  )

/** Entrants sans aucune part transmise : ce sont eux qui prennent des analogues. */
export const incomingWithoutTransfer = (values: RotationFormValues): number[] =>
  values.incomingProductIds.filter(
    (productId) => !values.transfers.some((cell) => cell.incomingProductId === productId),
  )

export const analoguesOf = (
  values: Pick<RotationFormValues, 'analogues'>,
  incomingProductId: number,
): number[] =>
  values.analogues
    .filter((link) => link.incomingProductId === incomingProductId)
    .map((link) => link.sourceProductId)

export const addAnalogue = (
  analogues: readonly AnalogueLink[],
  incomingProductId: number,
  sourceProductId: number,
): AnalogueLink[] =>
  analogues.some(
    (link) =>
      link.incomingProductId === incomingProductId && link.sourceProductId === sourceProductId,
  )
    ? [...analogues]
    : [...analogues, { incomingProductId, sourceProductId }]

export const removeAnalogue = (
  analogues: readonly AnalogueLink[],
  incomingProductId: number,
  sourceProductId: number,
): AnalogueLink[] =>
  analogues.filter(
    (link) =>
      link.incomingProductId !== incomingProductId || link.sourceProductId !== sourceProductId,
  )

export type RotationRoleKey = 'outgoing' | 'incoming'

export const addProduct = (
  values: RotationFormValues,
  role: RotationRoleKey,
  productId: number,
): RotationFormValues => {
  if (
    values.outgoingProductIds.includes(productId) ||
    values.incomingProductIds.includes(productId)
  ) {
    return values
  }
  return role === 'outgoing'
    ? { ...values, outgoingProductIds: [...values.outgoingProductIds, productId] }
    : { ...values, incomingProductIds: [...values.incomingProductIds, productId] }
}

/** Retire un produit de la rotation, avec ses cellules et, pour un entrant, ses analogues. */
export const removeProduct = (
  values: RotationFormValues,
  role: RotationRoleKey,
  productId: number,
): RotationFormValues =>
  role === 'outgoing'
    ? {
        ...values,
        outgoingProductIds: values.outgoingProductIds.filter((id) => id !== productId),
        transfers: values.transfers.filter((cell) => cell.sourceProductId !== productId),
      }
    : {
        ...values,
        incomingProductIds: values.incomingProductIds.filter((id) => id !== productId),
        transfers: values.transfers.filter((cell) => cell.incomingProductId !== productId),
        analogues: values.analogues.filter((link) => link.incomingProductId !== productId),
      }

/**
 * Corps envoyé à l'API (création, modification, aperçu). Les cellules suivent l'ordre de la
 * matrice (sortants puis entrants) : l'index d'une erreur `transfers.<i>.weight` y retrouve sa
 * cellule (`transferCellAt`). Un entrant à transferts n'envoie pas ses analogues (spec §4 : jamais
 * les deux). `null` sans date N.
 */
export const toRotationInput = (values: RotationFormValues): RotationInput | null => {
  if (values.rotationDate === null) return null
  const withTransfer = new Set(values.transfers.map((cell) => cell.incomingProductId))
  const transfers = values.outgoingProductIds.flatMap((sourceProductId) =>
    values.incomingProductIds.flatMap((incomingProductId) => {
      const percent = cellPercent(values.transfers, sourceProductId, incomingProductId)
      return percent === null
        ? []
        : [{ incomingProductId, sourceProductId, weight: percentToWeight(percent) }]
    }),
  )
  return {
    label: values.label.trim(),
    rotationDate: values.rotationDate,
    note: values.note.trim() === '' ? null : values.note.trim(),
    outgoingProductIds: [...values.outgoingProductIds],
    incomingProductIds: [...values.incomingProductIds],
    transfers,
    analogues: values.analogues.filter(
      (link) =>
        values.incomingProductIds.includes(link.incomingProductId) &&
        !withTransfer.has(link.incomingProductId),
    ),
  }
}

/** Cellule de la `index`-ième entrée `transfers` du corps envoyé (erreur 422 `transfers.<i>`). */
export const transferCellAt = (input: RotationInput, index: number): string | null => {
  const transfer = input.transfers.at(index)
  return transfer ? cellKey(transfer.sourceProductId, transfer.incomingProductId) : null
}

/** Erreurs 422 du serveur, rapportées aux champs et aux cartes du formulaire. */
export interface PlacedServerErrors {
  fields: Partial<Record<'label' | 'rotationDate' | 'note', string>>
  outgoing: string[]
  incoming: string[]
  /** Par cellule de la matrice (`cellKey`). */
  cells: Record<string, string>
  matrix: string[]
  analogues: string[]
  other: string[]
}

const emptyServerErrors = (): PlacedServerErrors => ({
  fields: {},
  outgoing: [],
  incoming: [],
  cells: {},
  matrix: [],
  analogues: [],
  other: [],
})

export const NO_SERVER_ERRORS = emptyServerErrors()

/**
 * Place les refus 422 (`{ field, message }`, chemins `rotationDate`, `transfers.0.weight`…) : sous
 * leur champ, sur leur cellule (l'index renvoie au corps envoyé), ou dans leur carte.
 */
export const placeServerErrors = (
  errors: readonly { field: string; message: string }[],
  input: RotationInput,
): PlacedServerErrors => {
  const placed = emptyServerErrors()
  for (const { field, message } of errors) {
    const [head, index] = field.split('.')
    if (head === 'label' || head === 'rotationDate' || head === 'note') {
      placed.fields[head] ??= message
    } else if (head === 'outgoingProductIds') placed.outgoing.push(message)
    else if (head === 'incomingProductIds') placed.incoming.push(message)
    else if (head === 'analogues') placed.analogues.push(message)
    else if (head === 'transfers') {
      const key = index === undefined ? null : transferCellAt(input, Number(index))
      if (key === null) placed.matrix.push(message)
      else placed.cells[key] ??= message
    } else placed.other.push(message)
  }
  return placed
}

export const fromRotationDto = (rotation: ProductRotationDto): RotationFormValues => ({
  label: rotation.label,
  rotationDate: rotation.rotationDate.slice(0, 10),
  note: rotation.note ?? '',
  outgoingProductIds: productsOfRole(rotation, 'outgoing').map((product) => product.productId),
  incomingProductIds: productsOfRole(rotation, 'incoming').map((product) => product.productId),
  transfers: rotation.links.flatMap((link) =>
    link.kind === 'transfer' && link.weight !== null
      ? [
          {
            sourceProductId: link.sourceProductId,
            incomingProductId: link.incomingProductId,
            percent: weightToPercent(link.weight),
          },
        ]
      : [],
  ),
  analogues: rotation.links.flatMap((link) =>
    link.kind === 'analogue'
      ? [{ incomingProductId: link.incomingProductId, sourceProductId: link.sourceProductId }]
      : [],
  ),
})

// ------------------------------------------------------------------------------------------------
// Dates
// ------------------------------------------------------------------------------------------------

const dayOf = (iso: string): DateTime => DateTime.fromISO(iso.slice(0, 10), { zone: PARIS })

/** `lun. 26/10/2026`. */
export const formatLongDay = (iso: string): string =>
  dayOf(iso).setLocale('fr').toFormat('ccc dd/MM/yyyy')

/** `lun. 26/10`. */
export const formatShortDay = (iso: string): string =>
  dayOf(iso).setLocale('fr').toFormat('ccc dd/MM')

/** `9 h 26`, heure de Paris d'un instant ISO. */
export const formatParisTime = (iso: string): string => {
  const time = DateTime.fromISO(iso).setZone(PARIS)
  return `${time.hour} h ${time.toFormat('mm')}`
}

/** `dim. 25/10 · 9 h 26`. */
export const formatComputeAt = (iso: string): string =>
  `${DateTime.fromISO(iso).setZone(PARIS).setLocale('fr').toFormat('ccc dd/MM')} · ${formatParisTime(iso)}`

// ------------------------------------------------------------------------------------------------
// Liste
// ------------------------------------------------------------------------------------------------

type RotationProducts = Pick<ProductRotationDto, 'products'>
type RotationWithLinks = Pick<ProductRotationDto, 'products' | 'links'>

export const productsOfRole = (rotation: RotationProducts, role: RotationRoleKey) =>
  rotation.products.filter((product) => product.role === role)

export const countByStatus = (
  rotations: readonly Pick<ProductRotationDto, 'status'>[],
): Record<RotationStatus, number> => {
  const counts: Record<RotationStatus, number> = { upcoming: 0, current: 0, done: 0 }
  for (const rotation of rotations) counts[rotation.status] += 1
  return counts
}

/** Recherche sur le nom de la rotation et sur le libellé ou la référence de ses produits. */
export const matchesRotationSearch = (
  rotation: Pick<ProductRotationDto, 'label' | 'products'>,
  search: string,
): boolean =>
  matchesTextSearch(
    [rotation.label, ...rotation.products.flatMap((product) => [product.label, product.reference])],
    search,
  )

/**
 * Colonne « Report de la demande » : remplacements 1→1, sortants répartis sur plusieurs entrants,
 * entrants sur analogues, sortants reportés au prorata. Une gamme entière répartie également se
 * résume en une ligne.
 */
export const transferSummaryOf = (rotation: RotationWithLinks): string[] => {
  const outgoing = productsOfRole(rotation, 'outgoing').map((product) => product.productId)
  const incoming = productsOfRole(rotation, 'incoming').map((product) => product.productId)
  const transfers = rotation.links.filter((link) => link.kind === 'transfer')
  const analogueIncoming = new Set(
    rotation.links.filter((link) => link.kind === 'analogue').map((link) => link.incomingProductId),
  )
  const linksFrom = (source: number) => transfers.filter((link) => link.sourceProductId === source)
  const linksTo = (target: number) => transfers.filter((link) => link.incomingProductId === target)

  const firstWeight = transfers[0]?.weight
  const evenlySpread =
    outgoing.length > 1 &&
    incoming.length > 1 &&
    transfers.length === outgoing.length * incoming.length &&
    transfers.every((link) => link.weight === firstWeight)

  const lines: string[] = []
  if (evenlySpread) lines.push('Gamme → gamme, répartie également')
  else {
    const oneToOne = transfers.filter(
      (link) =>
        link.weight === 1 &&
        linksFrom(link.sourceProductId).length === 1 &&
        linksTo(link.incomingProductId).length === 1,
    ).length
    const spread = outgoing.filter((id) => {
      const links = linksFrom(id)
      return links.length > 0 && !(links.length === 1 && links[0].weight === 1)
    }).length
    if (oneToOne > 0) lines.push(pluralize(oneToOne, 'remplacement 1→1', 'remplacements 1→1'))
    if (spread > 0) lines.push(pluralize(spread, 'sortant réparti', 'sortants répartis'))
  }
  if (analogueIncoming.size > 0) {
    lines.push(pluralize(analogueIncoming.size, 'entrant sur analogues', 'entrants sur analogues'))
  }
  const prorata = outgoing.filter((id) => linksFrom(id).length === 0).length
  if (prorata > 0)
    lines.push(pluralize(prorata, 'sortant reporté au prorata', 'sortants reportés au prorata'))
  return lines
}

// ------------------------------------------------------------------------------------------------
// Aperçu : contrôles, calendrier
// ------------------------------------------------------------------------------------------------

type PreviewAlert = RotationPreviewDto['alerts'][number]
export type AlertSeverity = PreviewAlert['severity']

export const hasBlockingAlert = (alerts: readonly PreviewAlert[]): boolean =>
  alerts.some((alert) => alert.severity === 'blocking')

/** `1 bloquant · 3 à vérifier`, `Aucun point bloquant`. */
export const alertsHeadline = (alerts: readonly PreviewAlert[]): string => {
  const blocking = alerts.filter((alert) => alert.severity === 'blocking').length
  const warnings = alerts.filter((alert) => alert.severity === 'warning').length
  const parts: string[] = []
  if (blocking > 0) parts.push(pluralize(blocking, 'bloquant'))
  if (warnings > 0) parts.push(`${warnings} à vérifier`)
  return parts.length > 0 ? parts.join(' · ') : 'Aucun point bloquant'
}

type PreviewProduct = RotationPreviewDto['products'][number]

/** `15 boutiques`, `2 boutiques et 1 projet`. */
export const storeCountLabel = (
  wave: Pick<RotationStoreException, 'storeIds' | 'storeProjectIds'>,
): string => {
  const parts: string[] = []
  if (wave.storeIds.length > 0) parts.push(pluralize(wave.storeIds.length, 'boutique'))
  if (wave.storeProjectIds.length > 0) parts.push(pluralize(wave.storeProjectIds.length, 'projet'))
  return parts.join(' et ')
}

/**
 * Résumé d'une vague par boutique, sous le produit dans les cartes Sortants et Entrants :
 * `15 boutiques : dernière livraison le 26/10`.
 */
export const storeWaveSummary = (wave: RotationStoreException): string => {
  const dates = [
    wave.launchDate && `lancement le ${frenchDayMonthOfIso(wave.launchDate)}`,
    wave.lastDeliveryDate && `dernière livraison le ${frenchDayMonthOfIso(wave.lastDeliveryDate)}`,
    wave.endDate && `arrêt le ${frenchDayMonthOfIso(wave.endDate)}`,
  ].filter(Boolean)
  return `${storeCountLabel(wave)} : ${dates.length > 0 ? dates.join(', ') : 'dates par défaut'}`
}

export type CalendarChipTone = 'compute' | 'delivery' | 'production' | 'stop' | 'stock' | 'muted'

export interface CalendarChip {
  label: string
  tone: CalendarChipTone
  /** Produits concernés quand ce n'est pas tout le groupe (catégories réappro différentes). */
  partial?: { count: number; total: number }
}

export interface CalendarRow {
  role: RotationRoleKey
  count: number
  /** Ligne d'en-tête : la date écrite par la rotation (`dernière livraison écrite : 25/10`). */
  written: string
  cells: Record<string, CalendarChip[]>
}

export interface RotationCalendar {
  days: string[]
  rotationDate: string
  rows: CalendarRow[]
}

interface CalendarEvent {
  day: string
  label: string
  tone: CalendarChipTone
}

/**
 * Seconde vague d'un produit à exceptions par boutique (amendement du 24/10) : sa dernière
 * livraison (sortant) ou son lancement (entrant) aux dates de ses boutiques.
 */
const waveEvents = (product: PreviewProduct): CalendarEvent[] =>
  (product.storeExceptions ?? []).flatMap((wave) => {
    const day = product.role === 'outgoing' ? wave.lastDeliveryDate : wave.launchDate
    if (day === null) return []
    const what = product.role === 'outgoing' ? 'dernière livraison' : '1re livraison'
    return [
      {
        day,
        label: `2e vague · ${what} (${storeCountLabel(wave)})`,
        tone: product.role === 'outgoing' ? 'stop' : 'delivery',
      } satisfies CalendarEvent,
    ]
  })

const outgoingEvents = (product: PreviewProduct, rotationDate: string): CalendarEvent[] => {
  const events: CalendarEvent[] = waveEvents(product)
  if (product.lastComputeAt) {
    events.push({
      day: DateTime.fromISO(product.lastComputeAt).setZone(PARIS).toISODate() ?? '',
      label: `Calcul ${formatParisTime(product.lastComputeAt)} · dernière commande`,
      tone: 'compute',
    })
  }
  if (product.lastActualDeliveryDate) {
    events.push({ day: product.lastActualDeliveryDate, label: 'Dernière livraison', tone: 'stop' })
  }
  events.push({ day: rotationDate, label: 'Vendus jusqu’à épuisement', tone: 'stock' })
  if (product.deducedEndDate) {
    events.push({ day: product.deducedEndDate, label: 'Fin de vente déduite', tone: 'stock' })
  }
  if (product.deactivationDate) {
    events.push({ day: product.deactivationDate, label: 'Désactivés', tone: 'muted' })
  }
  return events
}

const incomingEvents = (product: PreviewProduct): CalendarEvent[] => {
  const events: CalendarEvent[] = waveEvents(product)
  if (product.firstComputeAt) {
    events.push({
      day: DateTime.fromISO(product.firstComputeAt).setZone(PARIS).toISODate() ?? '',
      label: `Calcul ${formatParisTime(product.firstComputeAt)} · 1re commande`,
      tone: 'compute',
    })
  }
  if (product.firstProductionDate) {
    events.push({ day: product.firstProductionDate, label: 'Production', tone: 'production' })
  }
  if (product.firstDeliveryDate) {
    events.push({ day: product.firstDeliveryDate, label: '1re livraison', tone: 'delivery' })
  }
  return events
}

/** Au plus 10 colonnes : du premier évènement au dernier, N toujours compris. */
const MAX_CALENDAR_DAYS = 10

/**
 * Calendrier de l'aperçu : une ligne par rôle, une colonne par jour, les évènements de chaque
 * produit regroupés (un même calcul, une même livraison) ; un évènement qui ne vaut que pour une
 * partie des produits du groupe porte le compte.
 */
export const buildRotationCalendar = (
  preview: Pick<RotationPreviewDto, 'products'>,
  rotationDate: string,
): RotationCalendar => {
  const rows: CalendarRow[] = []
  const allDays = new Set<string>([rotationDate])

  for (const role of ['outgoing', 'incoming'] as const) {
    const products = preview.products.filter((product) => product.role === role)
    if (products.length === 0) continue
    const grouped = new Map<string, { event: CalendarEvent; count: number }>()
    for (const product of products) {
      const events =
        role === 'outgoing' ? outgoingEvents(product, rotationDate) : incomingEvents(product)
      for (const event of events) {
        if (event.day === '') continue
        const key = `${event.day}|${event.label}`
        const entry = grouped.get(key)
        if (entry) entry.count += 1
        else grouped.set(key, { event, count: 1 })
      }
    }
    const cells: Record<string, CalendarChip[]> = {}
    for (const { event, count } of grouped.values()) {
      allDays.add(event.day)
      cells[event.day] ??= []
      cells[event.day].push({
        label: event.label,
        tone: event.tone,
        ...(count < products.length ? { partial: { count, total: products.length } } : {}),
      })
    }
    rows.push({
      role,
      count: products.length,
      written:
        role === 'outgoing'
          ? `dernière livraison écrite : ${frenchDayMonthOfIso(addDaysIso(rotationDate, -1))}`
          : `lancement écrit : ${frenchDayMonthOfIso(rotationDate)}`,
      cells,
    })
  }

  const sorted = [...allDays].sort()
  const first = sorted[0]
  const last = sorted.at(-1) ?? first
  const days: string[] = []
  for (let day = first; day <= last && days.length < MAX_CALENDAR_DAYS; day = addDaysIso(day, 1)) {
    days.push(day)
  }
  return { days, rotationDate, rows }
}

// ------------------------------------------------------------------------------------------------
// Fiche produit et Prévisions
// ------------------------------------------------------------------------------------------------

type RotationForProduct = Pick<
  ProductRotationDto,
  'id' | 'label' | 'rotationDate' | 'deactivationDate' | 'products' | 'links'
>

const labelOf = (rotation: RotationProducts, productId: number): string =>
  rotation.products.find((product) => product.productId === productId)?.label ?? `#${productId}`

const weightedList = (
  rotation: RotationProducts,
  entries: readonly { productId: number; weight: number | null }[],
): string =>
  entries
    .map(({ productId, weight }) =>
      weight === null
        ? labelOf(rotation, productId)
        : `${labelOf(rotation, productId)} (${formatPercent(weightToPercent(weight))})`,
    )
    .join(', ')

export interface ProductRotationBanner {
  rotationId: number
  role: RotationRoleKey
  title: string
  detail: string
}

/**
 * Bandeaux de la fiche produit : « Sort de / Entre par la rotation « nom » le <N> », avec le
 * remplaçant et son poids, ou les sortants hérités et les analogues.
 */
export const productRotationBanners = (
  rotations: readonly RotationForProduct[],
  productId: number,
): ProductRotationBanner[] =>
  rotations.flatMap((rotation) =>
    rotation.products
      .filter((product) => product.productId === productId)
      .map((product): ProductRotationBanner => {
        const when = formatShortDay(rotation.rotationDate)
        if (product.role === 'outgoing') {
          const successors = rotation.links
            .filter((link) => link.kind === 'transfer' && link.sourceProductId === productId)
            .map((link) => ({ productId: link.incomingProductId, weight: link.weight }))
          const lastDelivery = frenchDayMonthOfIso(addDaysIso(rotation.rotationDate, -1))
          const deactivation = product.deactivationDate ?? rotation.deactivationDate
          return {
            rotationId: rotation.id,
            role: 'outgoing',
            title: `Sort de la rotation « ${rotation.label} » le ${when}`,
            detail: [
              successors.length > 0
                ? `Remplacé par ${weightedList(rotation, successors)}.`
                : 'Sans successeur : sa demande est reportée au prorata sur la catégorie.',
              `Dernière livraison le ${lastDelivery}, désactivé le ${frenchDayMonthOfIso(deactivation)}.`,
            ].join(' '),
          }
        }
        return {
          rotationId: rotation.id,
          role: 'incoming',
          title: `Entre par la rotation « ${rotation.label} » le ${when}`,
          detail: incomingDetailOf(rotation, productId),
        }
      }),
  )

/** Liens d'un entrant : ses sortants (transferts, avec leur poids) et ses analogues. */
const incomingLinksOf = (rotation: Pick<ProductRotationDto, 'links'>, productId: number) => ({
  sources: rotation.links
    .filter((link) => link.kind === 'transfer' && link.incomingProductId === productId)
    .map((link) => ({ productId: link.sourceProductId, weight: link.weight })),
  analogues: rotation.links
    .filter((link) => link.kind === 'analogue' && link.incomingProductId === productId)
    .map((link) => ({ productId: link.sourceProductId, weight: null })),
})

const incomingDetailOf = (rotation: RotationForProduct, productId: number): string => {
  const { sources, analogues } = incomingLinksOf(rotation, productId)
  if (sources.length > 0) {
    const inherited = sources.map(
      ({ productId: sourceId, weight }) =>
        `${formatPercent(weightToPercent(weight ?? 0))} de la demande de ${labelOf(rotation, sourceId)}`,
    )
    return `Hérite de ${inherited.join(', ')}.`
  }
  if (analogues.length > 0) {
    return `Part de départ : médiane de ${weightedList(rotation, analogues)} (analogues).`
  }
  return 'Sans prédécesseur ni analogue : part de départ à la médiane de la catégorie.'
}

export type LockedDateField = RotationProductDto['lockedFields'][number]

/**
 * Dates d'un produit fixées par une rotation (spec §3), avec le nom de la rotation qui les fixe.
 * Le backend dit lesquelles par membre (`lockedFields`) ; les rotations arrivent dans l'ordre de
 * l'API, N décroissant, et la plus récente l'emporte comme dans la garde : un produit entrant
 * d'une rotation puis sortant d'une autre a sa fin fixée par la seconde.
 */
export const dateLocksOf = (
  rotations: readonly Pick<ProductRotationDto, 'label' | 'products'>[],
  productId: number,
): Partial<Record<LockedDateField, string>> => {
  const locks: Partial<Record<LockedDateField, string>> = {}
  for (const rotation of rotations) {
    for (const product of rotation.products) {
      if (product.productId !== productId) continue
      for (const field of product.lockedFields) locks[field] ??= rotation.label
    }
  }
  return locks
}

/**
 * Date d'arrêt affichée d'un produit dont une rotation fixe la fin (`endLockedBy`) : vide en base,
 * la fin de vente se déduit de sa dernière livraison + max(1, DLC) + 1, la règle du backend
 * (`endDateFromLastDelivery`). `null` sinon.
 */
export const deducedEndDateOf = (
  product: { endDate: string | null; lastDeliveryDate: string | null; dlcDays: number | null },
  endLockedBy: string | undefined,
): string | null => {
  if (!endLockedBy || product.endDate || !product.lastDeliveryDate) return null
  return addDaysIso(product.lastDeliveryDate, Math.max(1, product.dlcDays ?? 0) + 1)
}

/**
 * Complément de l'infobulle New/Rec d'un entrant, page Prévisions (spec §8) : ses sortants et leur
 * poids, ou ses analogues, et la rotation. Clé : id du produit entrant.
 */
export const rotationLaunchNotes = (
  rotations: readonly RotationForProduct[],
): Map<number, string> => {
  const notes = new Map<number, string>()
  const ramp = `Part héritée pendant ${ROTATION_RAMP_DAYS} jours, puis part mesurée.`
  for (const rotation of rotations) {
    for (const product of productsOfRole(rotation, 'incoming')) {
      const id = product.productId
      const { sources, analogues } = incomingLinksOf(rotation, id)
      const named = `rotation « ${rotation.label} »`
      if (sources.length > 0) {
        notes.set(id, `Remplace ${weightedList(rotation, sources)}, ${named}. ${ramp}`)
      } else if (analogues.length > 0) {
        notes.set(
          id,
          `Démarre à la médiane de ${weightedList(rotation, analogues)}, ${named}. ${ramp}`,
        )
      } else {
        notes.set(id, `Entre par la ${named}, sans prédécesseur : médiane de la catégorie.`)
      }
    }
  }
  return notes
}
